import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, Package, ShieldCheck, ArrowLeft, Clock, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import QRCode from 'qrcode';
import { OABLineItem } from '../types/oab';
import { getPrintPortal, openCleanPrintWindow } from '../utils/printUtils';

export interface NriMissingItemDetail {
  codigo?: string;
  nombre: string;
  unidadMedida?: string;
  cantidadAprobada: number;
  cantidadRecibidaPrevia: number;
  cantidadRecibidaHoy: number;
  totalAcumulado: number;
  saldoPendiente: number;
  actionOnRemaining?: string;
  proyectoNombre?: string;
}

export interface NriRejectedSummaryItem {
  nombre: string;
  cantidadRechazada: number;
  unidad?: string;
  motivo?: string;
}

interface PrintSheetRampEntryProps {
  nriFolio?: string;
  offlineNriFolio?: string | null;
  actaRechazoFolio?: string | null;
  folioOAB: string;
  proveedor?: string;
  numeroNotaEntrega: string;
  fechaRecepcion: string;
  tipoDocumento?: string;
  receptorNombre?: string;
  choferNombre?: string;
  placaVehiculo?: string;
  tiendaDestino?: string;
  items: OABLineItem[];
  missingItems?: NriMissingItemDetail[];
  rejectedItems?: NriRejectedSummaryItem[];
  onClose: () => void;
}

export const PrintSheetRampEntry: React.FC<PrintSheetRampEntryProps> = ({
  nriFolio,
  offlineNriFolio,
  actaRechazoFolio,
  folioOAB,
  proveedor,
  numeroNotaEntrega,
  fechaRecepcion,
  tipoDocumento = 'NOTA_ENTREGA',
  receptorNombre = 'Operador de Rampa',
  choferNombre,
  placaVehiculo,
  tiendaDestino,
  items,
  missingItems,
  rejectedItems = [],
  onClose
}) => {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const printContentRef = useRef<HTMLDivElement>(null);

  const effectiveNriFolio = nriFolio || offlineNriFolio || `NRI-${(fechaRecepcion || '').replace(/-/g, '')}-01`;

  useEffect(() => {
    const el = getPrintPortal();
    setPortalElement(el);

    const prevTitle = document.title;
    document.title = `Nota_Recepcion_Interna_${effectiveNriFolio}_${folioOAB}`;

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://sanesca-inventario.pages.dev';
    const targetUrl = `${origin}/?rampReceipt=${encodeURIComponent(effectiveNriFolio)}&folio=${encodeURIComponent(folioOAB)}`;
    QRCode.toDataURL(targetUrl, { width: 112, margin: 1, errorCorrectionLevel: 'M' })
      .then(url => setQrDataUrl(url))
      .catch(() => setQrDataUrl(''));

    return () => {
      document.title = prevTitle;
    };
  }, [effectiveNriFolio, folioOAB]);

  const handlePrint = () => {
    window.print();
  };

  const handleOpenCleanWindow = () => {
    if (!printContentRef.current) return;
    openCleanPrintWindow({
      title: `Nota_Recepcion_Interna_${effectiveNriFolio}_${folioOAB}`,
      contentHtml: printContentRef.current.innerHTML,
      pageSize: 'letter portrait',
      margin: '6mm 8mm'
    });
  };

  // Filtrar renglones recibidos hoy (> 0) para Sección A
  const receivedTodayItems = items.filter(it => (it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0) > 0);

  // Construir Sección B (Saldos Pendientes / Backorder al Corte), incluyendo ítems con 0 recibido hoy
  const computedMissingItems: NriMissingItemDetail[] = missingItems
    ? missingItems
    : items
        .map(it => {
          const apr = Number(it.cantidadAprobada || it.cantidadSolicitada || 0);
          const prev = Number(it.cantidadRecibidaPrevia || 0);
          const recHoy = Number(it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0);
          const totalAcum = prev + recHoy;
          const saldo = Math.max(0, apr - totalAcum);
          return {
            codigo: it.codigo,
            nombre: it.nombre,
            unidadMedida: it.unidadMedida || 'und',
            cantidadAprobada: apr,
            cantidadRecibidaPrevia: prev,
            cantidadRecibidaHoy: recHoy,
            totalAcumulado: totalAcum,
            saldoPendiente: saldo,
            actionOnRemaining: it.actionOnRemaining || 'BACKORDER',
            proyectoNombre: it.proyectoNombre
          };
        })
        .filter(m => m.saldoPendiente > 0);

  const totalRecibido = receivedTodayItems.reduce((sum, it) => sum + (it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0), 0);
  const totalBultos = receivedTodayItems.reduce((sum, it) => sum + (it.bultos || 1), 0);
  const totalPendiente = computedMissingItems
    .filter(m => (m.actionOnRemaining || 'BACKORDER') === 'BACKORDER')
    .reduce((sum, m) => sum + m.saldoPendiente, 0);
  const totalRechazado = rejectedItems.reduce((sum, r) => sum + (r.cantidadRechazada || 0), 0);
  const effectiveActaRechazo = actaRechazoFolio || (totalRechazado > 0 ? `RECHAZO-${effectiveNriFolio}` : null);

  const docLabel = tipoDocumento === 'SENIAT' ? 'Factura SENIAT' :
                   tipoDocumento === 'FLETE_INTERNO' ? 'Guía Traslado Interno' :
                   tipoDocumento === 'SN' ? 'Recepción S/N' : 'Nota de Entrega';

  const content = (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col items-center overflow-y-auto p-2 sm:p-4 md:py-6 font-sans text-slate-800 print-ramp-root print:static print:p-0 print:m-0 print:bg-white print:overflow-visible">
      <style>{`
        @media print {
          @page {
            size: letter portrait;
            margin: 6mm 8mm;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .print-ramp-root {
            position: static !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }
          .print-ramp-card {
            width: 100% !important;
            max-width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      {/* Barra de Controles superior (no se imprime) */}
      <div className="no-print w-full max-w-4xl bg-slate-900 text-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 flex items-center justify-between border border-slate-800">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-300 hover:text-white transition"
            title="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-400" />
              Nota de Recepción Interna (NRI) — Almacén y Taller
            </h2>
            <p className="text-xs text-slate-400">
              Folio NRI: <span className="font-mono text-emerald-300 font-bold">{effectiveNriFolio}</span> · OAB: <span className="font-mono text-sky-300">{folioOAB}</span> · {docLabel}: {numeroNotaEntrega || 'S/N'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenCleanWindow}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-sky-300 hover:text-sky-200 border border-slate-700 transition active:scale-95 shadow-md"
            title="Abrir en ventana limpia para tablets o navegadores con diálogo de impresión recortado"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Ventana Limpia</span>
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg shadow-sm transition active:scale-95 text-xs sm:text-sm"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir NRI (Carta)</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hoja Física Carta (A4/Letter Estricto) */}
      <div 
        ref={printContentRef}
        className="w-full max-w-4xl bg-white border border-slate-300 rounded-lg p-6 sm:p-8 shadow-sm print-ramp-card print:p-0 print:border-0 print:shadow-none print:w-full"
      >
        {/* Cabecera Oficial con QR */}
        <div className="border-b-2 border-slate-800 pb-3 mb-4 flex justify-between items-start gap-4 avoid-break">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-emerald-700 text-white flex items-center justify-center font-bold text-lg rounded">
                S
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">SANESCA PRO</h1>
            </div>
            <p className="text-xs text-slate-700 mt-1 uppercase tracking-wider font-bold">
              Nota de Recepción Interna (NRI) — Muelle de Descarga y Custodia de Almacén
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Documento de Control Físico Interno (Confidencialidad de Costos Aplicada — Uso Almacén / Taller / Compras)
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="text-right">
              <span className="inline-block px-3 py-1 bg-emerald-50 border border-emerald-400 text-emerald-900 font-mono text-sm font-black rounded">
                {effectiveNriFolio}
              </span>
              {offlineNriFolio && offlineNriFolio !== effectiveNriFolio && (
                <p className="text-[10px] font-mono text-slate-500 mt-0.5">
                  Alias Offline: {offlineNriFolio}
                </p>
              )}
              <p className="text-xs text-slate-700 mt-1 font-mono font-bold">
                Orden: {folioOAB}
              </p>
              <p className="text-[11px] text-slate-600 mt-0.5 font-medium">
                Fecha: <span className="font-semibold text-slate-800">{fechaRecepcion}</span>
              </p>
            </div>
            {qrDataUrl && (
              <div className="border border-slate-300 rounded p-1 bg-white shrink-0 text-center">
                <img src={qrDataUrl} alt={`QR ${effectiveNriFolio}`} className="w-16 h-16 object-contain" />
                <span className="block text-[8px] font-mono text-slate-500 uppercase">Verificar NRI</span>
              </div>
            )}
          </div>
        </div>

        {/* Metadatos de la Entrada */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs mb-3 avoid-break">
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Proveedor:</span>
            <span className="font-bold text-slate-900 text-xs truncate block">{proveedor || 'Proveedor General'}</span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Documento de Respaldo:</span>
            <span className="font-bold text-slate-900 text-xs block">
              {docLabel} <span className="font-mono text-blue-700">#{numeroNotaEntrega || 'S/N'}</span>
            </span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Receptor en Rampa:</span>
            <span className="font-bold text-slate-900 text-xs block">{receptorNombre}</span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Transporte / Placa:</span>
            <span className="font-semibold text-slate-800 text-xs block">
              {[choferNombre, placaVehiculo].filter(Boolean).join(' · ') || 'No especificado'}
            </span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Destino / Obra:</span>
            <span className="font-bold text-slate-900 text-xs truncate block">{tiendaDestino || 'Segregado por renglón'}</span>
          </div>
          <div>
            <span className="text-slate-500 block uppercase font-medium text-[10px]">Resumen del Corte:</span>
            <span className="font-bold text-emerald-700 text-xs block font-mono">
              +{totalRecibido} rec ({totalBultos} bto) · {totalPendiente} pend
            </span>
          </div>
        </div>

        {/* Cintillo de Referencia Cruzada si hubo Rechazo en este viaje (Nodo 2/4) */}
        {totalRechazado > 0 && (
          <div className="p-2.5 bg-rose-50 border border-rose-300 rounded-lg text-xs text-rose-900 mb-3 flex items-start gap-2.5 avoid-break">
            <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0 mt-0.5" />
            <div>
              <strong>Referencia Cruzada a Acta de No Conformidad ({effectiveActaRechazo}):</strong> En esta recepción se devolvieron al transportista <strong>{totalRechazado} unidad(es)</strong> en <strong>{rejectedItems.length} renglón(es)</strong> por defectos o discrepancias físicas. Dichas unidades <strong>NO ingresaron al inventario</strong> y constan en el documento legal separado <code>{effectiveActaRechazo}</code>.
            </div>
          </div>
        )}

        {/* SECCIÓN A: Mercancía Recibida Conforme Hoy */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5 avoid-break">
            <h3 className="text-xs font-black uppercase tracking-wider text-emerald-900 flex items-center gap-1.5">
              <span>✅ Sección A — Mercancía Recibida Conforme e Ingresada Hoy ({receivedTodayItems.length} renglones)</span>
            </h3>
            <span className="text-[11px] font-mono font-bold text-emerald-700">
              Total Ingresado Hoy: {totalRecibido} und · {totalBultos} bulto(s)
            </span>
          </div>

          {receivedTodayItems.length === 0 ? (
            <div className="p-2.5 border border-slate-200 rounded bg-slate-50 text-xs text-slate-600 italic avoid-break">
              No se ingresaron unidades conformes en este viaje (Recepción con rechazo total o registro en cero).
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b-2 border-slate-800 bg-slate-100 text-slate-700 font-bold uppercase tracking-wider text-[10px] avoid-break">
                    <th className="py-1.5 px-2 text-center w-8">#</th>
                    <th className="py-1.5 px-2">Descripción del Insumo / SKU</th>
                    <th className="py-1.5 px-2 text-center">Und</th>
                    <th className="py-1.5 px-2 text-right">Aprobado</th>
                    <th className="py-1.5 px-2 text-right">Rec. Previo</th>
                    <th className="py-1.5 px-2 text-right text-emerald-800 bg-emerald-50">Recibido Hoy</th>
                    <th className="py-1.5 px-2 text-center">Bultos</th>
                    <th className="py-1.5 px-2 text-left">Destino en Planta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {receivedTodayItems.map((it, idx) => {
                    const recHoy = Number(it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0);
                    const prev = Number(it.cantidadRecibidaPrevia || 0);
                    const apr = Number(it.cantidadAprobada || it.cantidadSolicitada || 0);
                    const isMTO = it.proyectoNombre && !it.proyectoNombre.toLowerCase().includes('stock general');

                    return (
                      <tr key={it.id || idx} className="hover:bg-slate-50 avoid-break">
                        <td className="py-1.5 px-2 text-center font-mono text-slate-400 text-[11px]">{idx + 1}</td>
                        <td className="py-1.5 px-2">
                          <div className="font-bold text-slate-900 text-xs leading-tight">{it.nombre}</div>
                          {it.codigo && <span className="font-mono text-[9px] text-slate-500">{it.codigo}</span>}
                        </td>
                        <td className="py-1.5 px-2 text-center text-slate-600 uppercase font-mono text-[10.5px]">{it.unidadMedida || 'und'}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600 text-[10.5px]">{apr}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-500 text-[10.5px]">{prev}</td>
                        <td className="py-1.5 px-2 text-right font-mono font-bold text-emerald-700 bg-emerald-50/60 text-[11px]">
                          +{recHoy}
                        </td>
                        <td className="py-1.5 px-2 text-center font-mono text-slate-700 text-[10.5px]">{it.bultos || 1}</td>
                        <td className="py-1.5 px-2">
                          {isMTO ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                              🎯 {it.proyectoNombre}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-medium bg-slate-100 text-slate-700">
                              📦 Stock Común (Gaveta)
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SECCIÓN B: Saldos Pendientes por Llegar / Backorder al Corte */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5 avoid-break">
            <h3 className="text-xs font-black uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-700" />
              <span>⏳ Sección B — Saldos Pendientes por Llegar / Backorder al Corte ({computedMissingItems.length} renglones)</span>
            </h3>
            <span className="text-[11px] font-mono font-bold text-amber-700">
              Backorder Pendiente: {totalPendiente} und
            </span>
          </div>

          {computedMissingItems.length === 0 ? (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900 flex items-center gap-2 font-medium avoid-break">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Orden 100% Completada al cierre de esta recepción — Sin saldos ni backorders pendientes por llegar.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b-2 border-amber-800 bg-amber-50/70 text-amber-950 font-bold uppercase tracking-wider text-[10px] avoid-break">
                    <th className="py-1.5 px-2 text-center w-8">#</th>
                    <th className="py-1.5 px-2">Insumo con Saldo Pendiente</th>
                    <th className="py-1.5 px-2 text-center">Und</th>
                    <th className="py-1.5 px-2 text-right">Aprobado</th>
                    <th className="py-1.5 px-2 text-right">Rec. Hoy</th>
                    <th className="py-1.5 px-2 text-right">Acumulado</th>
                    <th className="py-1.5 px-2 text-right text-amber-900 bg-amber-100/70">Faltante</th>
                    <th className="py-1.5 px-2 text-left">Disposición</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {computedMissingItems.map((m, idx) => {
                    const actionLabel = m.actionOnRemaining === 'REQUOTE_ANOTHER_VENDOR'
                      ? '🔄 Re-cotizar Otro Prov.'
                      : m.actionOnRemaining === 'DISCARD'
                      ? '🚫 Saldo Descartado'
                      : '⏳ En Backorder (Próx. Flete)';

                    return (
                      <tr key={idx} className="hover:bg-amber-50/20 avoid-break">
                        <td className="py-1.5 px-2 text-center font-mono text-slate-400 text-[11px]">{idx + 1}</td>
                        <td className="py-1.5 px-2">
                          <span className="font-bold text-slate-900 text-xs leading-tight">{m.nombre}</span>
                          {m.codigo && <span className="ml-1.5 font-mono text-[9px] text-slate-500">({m.codigo})</span>}
                        </td>
                        <td className="py-1.5 px-2 text-center font-mono uppercase text-slate-600 text-[10.5px]">{m.unidadMedida || 'und'}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600 text-[10.5px]">{m.cantidadAprobada}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600 text-[10.5px]">{m.cantidadRecibidaHoy}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-700 font-semibold text-[10.5px]">{m.totalAcumulado}</td>
                        <td className="py-1.5 px-2 text-right font-mono font-black text-amber-800 bg-amber-50 text-[11px]">
                          {m.saldoPendiente}
                        </td>
                        <td className="py-1.5 px-2 text-[10.5px] font-semibold text-slate-700">
                          {actionLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mensaje de Custodia e Instrucción de Taller */}
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded text-[10.5px] text-amber-900 mb-4 flex items-start gap-2 avoid-break">
          <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <strong>Instrucción Operativa:</strong> Esta Nota de Recepción Interna (<code>{effectiveNriFolio}</code>) respalda el ingreso físico en almacén. Los materiales marcados como <strong>MTO</strong> deben trasladarse a la zona de ensamble de la obra/tienda indicada sin mezclar con stock común.
          </div>
        </div>

        {/* Firmas de Custodia Interna */}
        <div className="grid grid-cols-2 gap-10 pt-4 border-t border-slate-300 text-xs avoid-break">
          <div className="text-center">
            <div className="border-b border-slate-400 h-10 mb-1.5"></div>
            <p className="font-bold text-slate-800 text-xs">Recibido en Muelle por: {receptorNombre}</p>
            <p className="text-[9.5px] text-slate-500">Firma y Cédula del Receptor de Rampa (Almacén)</p>
          </div>
          <div className="text-center">
            <div className="border-b border-slate-400 h-10 mb-1.5"></div>
            <p className="font-bold text-slate-800 text-xs">Recibido Conforme en Taller / Custodia</p>
            <p className="text-[9.5px] text-slate-500">Firma del Jefe de Almacén o Maestro de Taller</p>
          </div>
        </div>
      </div>
    </div>
  );

  return portalElement ? createPortal(content, portalElement) : null;
};
